<template>
  <!--
    P3 (student-foundation-redesign) -- AI Tutor hero + quick actions.
    Geometry/gradient/typography measured directly from the RENDERED
    prototype (00_*.md §12 PF-09; 02_*.md §12), not its source.

    WP-04V-E (D7): the Coming Soon badge is REMOVED. P3 required it because
    the tutor was not built; it shipped in `WP-03`/`WP-03A` and the hero's
    destinations now reach the real assistant, so the badge had become an
    untrue claim. This narrowly supersedes PF-11 #6 for this badge only --
    the Advisor card keeps its own badge, which is still true.
  -->
  <section class="ai-tutor-hero" aria-labelledby="ai-tutor-hero-title">
    <span class="ai-tutor-hero__glow" aria-hidden="true"></span>

    <div class="ai-tutor-hero__intro">
      <span class="ai-tutor-hero__icon" aria-hidden="true">
        <student-home-icon name="spark" :size="20" />
      </span>
      <span class="ai-tutor-hero__badge-row">
        <span>{{ $t('studentHome.aiHero.badge') }}</span>
      </span>
    </div>

    <div class="ai-tutor-hero__main">
      <div class="ai-tutor-hero__copy">
        <h2 id="ai-tutor-hero-title" class="ai-tutor-hero__heading">
          {{ $t('studentHome.aiHero.heading') }}
        </h2>
        <p class="ai-tutor-hero__body">{{ $t('studentHome.aiHero.body') }}</p>
      </div>

      <router-link
        :to="`/${$i18n.locale}/student/ai-tutor`"
        class="ai-tutor-hero__cta"
        data-testid="ai-hero-cta"
        @click.native="onStartConversation"
      >
        {{ $t('studentHome.aiHero.cta') }}
        <student-home-icon name="arrow" :size="18" />
      </router-link>
    </div>

    <div
      class="ai-tutor-hero__quick-actions"
      role="group"
      :aria-label="$t('studentHome.quickActions.ariaLabel')"
    >
      <router-link
        v-for="intent in intents"
        :key="intent.key"
        :to="{
          path: `/${$i18n.locale}/student/ai-tutor`,
          query: { intent: intent.key },
        }"
        class="ai-tutor-hero__quick-action"
        :data-testid="`ai-quick-action-${intent.key}`"
        @click.native="onStartConversation"
      >
        <student-home-icon :name="intent.icon" :size="18" />
        <span>{{ intent.label }}</span>
      </router-link>
    </div>
  </section>
</template>

<script>
import StudentHomeIcon from './StudentHomeIcon.vue'

export default {
  name: 'AiTutorHero',
  components: { StudentHomeIcon },
  methods: {
    // Student Journey V2 -- every entry on this card is "New Conversation".
    // Leaving a chat and coming back here must not drop the student into the
    // conversation they just left: clear the open conversation first so the
    // tutor route renders the setup. Existing sessions stay (they are listed in
    // Reports); the setup then creates a NEW one.
    onStartConversation() {
      this.$store.dispatch('aiTutor/beginNewConversation')
    },
  },
  computed: {
    intents() {
      return [
        {
          key: 'troubleshoot',
          icon: 'chat',
          label: this.$t('studentHome.quickActions.troubleshoot'),
        },
        {
          key: 'review',
          icon: 'check',
          label: this.$t('studentHome.quickActions.review'),
        },
        {
          key: 'learn',
          icon: 'book',
          label: this.$t('studentHome.quickActions.learn'),
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
// Student Journey V2: every value below was re-measured from the RENDERED
// `borna-student-journey-v2.html` `.ai-home-hero` at 1440 / 700 / 390 (00_*.md
// §18); the phone-tier rules are the reference's own `@media (width<=620px)`.
// The gradient, glow and shadow are unchanged from the earlier measurement --
// the reference draws them identically. The Coming Soon badge stays removed
// (WP-04V-E D7): the tutor is real, so it would be an untrue claim.
$ai-hero-text: #ffffff;
$ai-hero-muted: #dbe4ff;

.ai-tutor-hero {
  position: relative;
  isolation: isolate;
  min-height: 272px;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 28px;
  background: linear-gradient(132deg, #102665 0%, #2948bd 56%, #118ca7 130%);
  box-shadow: 0 22px 48px rgba(29, 53, 139, 0.22);
  color: $ai-hero-text;
  overflow: hidden;

  @include borna-phone {
    min-height: 0;
    padding: 15px;
    border-radius: 20px;
  }

  @media screen and (max-width: 360.98px) {
    padding: 13px;
  }
}

.ai-tutor-hero__glow {
  position: absolute;
  z-index: -1;
  top: -110px;
  left: -74px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(70, 218, 231, 0.34),
    rgba(70, 218, 231, 0) 70%
  );
  pointer-events: none;
}

.ai-tutor-hero__intro {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #dce7ff;
  font-size: 13px;
  font-weight: 800;

  @include borna-phone {
    font-size: 11px;
  }
}

.ai-tutor-hero__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 45px;
  height: 45px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
  color: $ai-hero-text;

  @include borna-phone {
    width: 36px;
    height: 36px;
    border-radius: 12px;
  }
}

.ai-tutor-hero__badge-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-tutor-hero__main {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 24px;
  margin-top: 18px;

  @include borna-phone {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-top: 10px;
  }
}

.ai-tutor-hero__heading {
  // This heading sits on the hero's dark gradient, so it opts out of the
  // student-surface default heading ink.
  max-width: 580px;
  margin: 0 0 7px;
  color: $ai-hero-text;
  font-size: $borna-fs-h2-hero;
  font-weight: $borna-fw-regular;
  letter-spacing: -0.045em;
  line-height: 1.45;

  @include borna-phone {
    margin-bottom: 3px;
    font-size: $borna-fs-h1-home-phone;
    line-height: 1.42;
  }

  @media screen and (max-width: 360.98px) {
    font-size: 19px;
  }
}

.ai-tutor-hero__body {
  max-width: 580px;
  margin: 0;
  color: $ai-hero-muted;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.9;

  @include borna-phone {
    font-size: 13px;
    line-height: 1.7;
  }

  @media screen and (max-width: 360.98px) {
    font-size: 12px;
  }
}

// The New Conversation entry (see `onStartConversation`).
.ai-tutor-hero__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  min-width: 145px;
  min-height: 48px;
  padding: 0 19px;
  border: 1px solid #fff;
  border-radius: 14px;
  background: #fff;
  color: $borna-indigo-900;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 12px 26px rgba(2, 15, 65, 0.22);
  text-decoration: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: #f3f7ff;
    text-decoration: none;
  }

  @include borna-phone {
    width: 100%;
    min-height: 42px;
  }
}

.ai-tutor-hero__quick-actions {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
  margin-top: 23px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);

  @include borna-phone {
    gap: 6px;
    margin-top: 12px;
    padding-top: 10px;
  }
}

.ai-tutor-hero__quick-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  min-width: 0;
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.09);
  color: #fff;
  font-size: 13px;
  font-weight: 750;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;

  ::v-deep svg {
    flex: none;
    color: #9aebf2;
  }

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.4);
    text-decoration: none;
  }

  @include borna-phone {
    flex-direction: column;
    gap: 3px;
    min-height: 52px;
    padding: 5px 3px;
    border-radius: 11px;
    font-size: 10.5px;
    line-height: 1.35;
    text-align: center;
  }

  @media screen and (max-width: 360.98px) {
    min-height: 49px;
    font-size: 9.5px;
  }
}

// The "arrow" glyph (StudentHomeIcon) is copied directly from the rendered
// prototype's own RTL output and already points the correct direction there.

// P43-02 (Owner UAT): this is a Forward / Continue arrow: «←» in Persian (the
// glyph as drawn), «→» in English.
html:not(.is-rtl) .ai-tutor-hero__cta ::v-deep svg {
  transform: scaleX(-1);
}
</style>
